import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FilterX, History, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { aceitoDaUrl, FiltroParceiros, FiltroPeriodo, periodoDaUrl, uuidDaUrl, uuidsDaUrl, limparFiltrosEFocarBusca } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { data, dinheiro, nomeParceiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_COMPRA, ESTADOS_COMPRA, type Compra } from './compra-tipos';
import { DetalheCompra } from './DetalheCompra';
import { FormCompra } from './FormCompra';

/** Tipos do filtro (W36): os que a API aceita em `tipo`. */
const TIPOS_FILTRO = ['produto', 'servico'] as const;

/**
 * Fase 2 (W8, B §9.4 camada 1): aba Compras, a compra direta. Existe nos dois modos de operação. No simplificado é a única
 * aba do módulo e leva ao histórico do fluxo detalhado (só leitura). Filtros na URL.
 */
export function Compras() {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [nova, setNova] = useState(false);
  // Review r29 robustez (O2): `?compra=` que não é uuid é ignorado (antes a ficha abria com 422 e "Não foi possível carregar").
  const [aberta, setAberta] = useState<string | null>(uuidDaUrl(busca.get('compra')) || null);
  useEffect(() => {
    const id = uuidDaUrl(busca.get('compra'));
    if (id) setAberta(id);
  }, [busca]);
  const filtro = (k: string) => busca.get(k) ?? '';
  const mudarVarios = (parcial: Record<string, string>) => setBusca((atual) => filtrosNaUrl(atual, parcial), { replace: true });
  const mudar = (k: string, v: string) => mudarVarios({ [k]: v });
  // Fase 2 (W36): tipo (produto ou serviço), fornecedores com chips e o período por competência no `SeletorPeriodo`.
  // Review r27 spec (B1): valor que a API recusaria (`?tipo=xx`, fornecedor que não é uuid, data inválida) é ignorado, como
  // nas Movimentações; antes ia cru e a lista caía em "Indisponível" (422).
  // Review r28 spec (B1): o par de datas só vale junto (o mesmo no `FiltroPeriodo` e na API).
  const periodo = periodoDaUrl(filtro('de'), filtro('ate'));
  const params = {
    estado: aceitoDaUrl(filtro('estado'), ESTADOS_COMPRA) || undefined,
    tipo: aceitoDaUrl(filtro('tipo'), TIPOS_FILTRO) || undefined,
    fornecedor_id: uuidsDaUrl(filtro('fornecedor_id')).join(',') || undefined,
    de: periodo.de || undefined,
    ate: periodo.ate || undefined,
  };
  const filtrando = !!(params.estado || params.tipo || params.fornecedor_id || params.de || params.ate);
  const simplificado = s.modo('compras.modo') === 'simplificado';

  const colunas: Coluna<Compra>[] = [
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, ordenavel: true, render: (c) => <span className="font-medium">{c.numero}</span> },
    {
      chave: 'fornecedor_nome',
      rotulo: t('compra.fornecedor'),
      principal: true,
      render: (c) => (
        <span className="flex min-w-0 max-w-sm flex-col">
          <span className="truncate font-medium" title={nomeParceiro(c.fornecedor_nome)}>
            {nomeParceiro(c.fornecedor_nome)}
          </span>
          <TextoCortado className="text-xs text-tinta-3">{textoLivre(c.descricao, c.fornecedor_nome)}</TextoCortado>
        </span>
      ),
    },
    { chave: 'tipo', rotulo: t('c3.tipo'), render: (c) => (c.tipo ? t(`c3.tipoLista.${c.tipo}`) : '') },
    { chave: 'competencia', rotulo: t('compra.competencia'), ordenavel: true, mono: true, render: (c) => data(c.competencia) },
    { chave: 'nf', rotulo: t('compra.nf'), oculta: true, mono: true, render: (c) => c.nf_numero ?? '' },
    { chave: 'parcelas', rotulo: t('compra.parcelas'), alinhar: 'direita', mono: true, render: (c) => String(c.parcelas) },
    { chave: 'total', rotulo: t('campos.total'), ordenavel: true, alinhar: 'direita', mono: true, render: (c) => dinheiro(c.total_centavos, c.moeda) },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (c) => data(c.created_at) },
    { chave: 'estado', rotulo: t('campos.estado'), render: (c) => <Selo cor={COR_COMPRA[c.estado]}>{t(`compra.estado.${c.estado}`)}</Selo> },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AcaoPrimaria>
        {s.pode('compras.excluir') && <BotaoLixeira entidades={['compras.compra']} />}
        {s.pode('compras.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNova(true)}>
            {t('compra.nova')}
          </Botao>
        )}
      </AcaoPrimaria>

      <section aria-label={tn('comum.filtrar')} className="grid grid-cols-2 items-end gap-2 sm:flex sm:flex-wrap">
        <div className="col-span-2 sm:col-span-1 sm:w-56">
          <Selecao aria-label={t('campos.estado')} vazio={t('compra.todosEstados')} opcoes={ESTADOS_COMPRA.map((e) => ({ valor: e, rotulo: t(`compra.estado.${e}`) }))} value={params.estado ?? ''} onChange={(e) => mudar('estado', e.target.value)} />
        </div>
        <div className="col-span-2 sm:col-span-1 sm:w-56">
          <Selecao aria-label={t('compra.tipoFiltro.rotulo')} vazio={t('compra.tipoFiltro.todos')} opcoes={TIPOS_FILTRO.map((x) => ({ valor: x, rotulo: t(`compra.tipoFiltro.${x}`) }))} value={params.tipo ?? ''} onChange={(e) => mudar('tipo', e.target.value)} />
        </div>
        <FiltroPeriodo bases={['competencia']} campo="" de={periodo.de} ate={periodo.ate} aoMudar={({ de, ate }) => mudarVarios({ de, ate })} />
        <FiltroParceiros papel="fornecedor" ids={uuidsDaUrl(filtro('fornecedor_id'))} aoMudar={(ids) => mudar('fornecedor_id', ids.join(','))} />
        <Botao className="col-span-2 whitespace-nowrap sm:col-span-1" variante="fantasma" icone={<FilterX className="size-4" />} disabled={!filtrando} onClick={() => limparFiltrosEFocarBusca(() => setBusca({}, { replace: true }))} data-limpar-filtros="">
          {t('filtros.limpar')}
        </Botao>
        {simplificado && (
          <Link to="/operacao/compras/pedidos" className="col-span-2 ml-auto inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm text-tinta-2 hover:bg-sup-2 hover:text-tinta-1">
            <History className="size-4" aria-hidden />
            {t('historico.verHistorico')}
          </Link>
        )}
      </section>

      <Tabela<Compra>
        tela="compras.compras"
        caminho="/compras/compras"
        params={params}
        filtros={{ estado: filtro('estado'), tipo: filtro('tipo'), fornecedor_id: filtro('fornecedor_id'), de: filtro('de'), ate: filtro('ate') }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        entidade="compras.compra"
        aoClicar={(c) => setAberta(c.id)}
        vazio={<Vazio texto={filtrando ? t('compra.vazioFiltro') : t('compra.vazio')} />}
      />

      <FormCompra aberto={nova} aoFechar={() => setNova(false)} aoSalvar={(id) => setAberta(id)} />
      <DetalheCompra
        id={aberta}
        aoFechar={() => {
          setAberta(null);
          if (busca.get('compra')) mudar('compra', '');
        }}
      />
    </div>
  );
}
